import React, { useMemo } from 'react';
import iamCoreClient from './lib/iam-core-client';
import OktaSignInWidget from './OktaSignInWidget';
import { useOktaAuth } from '@okta/okta-react';
import ThrottleError from './ThrottleError';
import { shouldThrottle } from './lib/throttle-utils';
import Loader from './Loader';
import sessionStorageUtil from './lib/session-storage-util';
import { logPostAuthErrorEvent, logThrottleErrorEvent, logFhirRequestErrorEvent } from './lib/amplitude-utils';
import {
  enrichApiResponse,
  parsedPayload,
  getPostAuthorizeParams
} from './lib/authorize-utils';
import { useQuery } from './lib/router-utils';
import { useFramingContext } from './FrameRestricter';
import KeysProber from './KeysProber';
import { addHTI1Scopes } from './lib/scope-mapping-utils';
import URI from "urijs";
import { FhirRequestError } from './lib/errors';
import { logger, flushLogs } from './lib/logger';

const POST_AUTHN_DEGRADE_REQUEST = 'postAuthnDegradeRequest';
const POST_AUTHN_FHIR_REQUEST_ERROR = 'postAuthnFhirRequestError';

function _fetchData( postAuthnInfo, accessToken ) {
  //swagger 2.0 doesn't like nulls'
  postAuthnInfo = Object.fromEntries(Object.entries(postAuthnInfo).filter(([ _, v ]) => v != null));
  return iamCoreClient.fetchPostAuthnConfig({ postAuthnInfo }, accessToken);
}
export default function PostAuthorize() {
  const { authState, oktaAuth } = useOktaAuth();
  const [ postAuthnConfigs, setPostAuthnConfigs ] = React.useState();
  const [ postAuthnConfigFetchError, setPostAuthnConfigFetchError ] = React.useState();
  const { allowFraming } = useFramingContext();

  const { state } = getPostAuthorizeParams();
  let authorizePostParams;
  if (!!state) {
    const { authorizePostParamsObject } = JSON.parse(state);
    authorizePostParamsObject.scope = addHTI1Scopes(authorizePostParamsObject.scope);
    authorizePostParams = new URLSearchParams(authorizePostParamsObject);
  }
  const searchParams = useQuery(authorizePostParams);
  const payload = useMemo(() => parsedPayload(searchParams), [searchParams]);
  const {
    clientId,
    scopeString,
    redirectUri,
    aud,
    emailToken,
    idp,
    fhir,
    authServerId,
    lwaUniqId,
    uiLocales,
  } = payload;
  const [ isThrottled, setIsThrottled ] = React.useState(false);
  const [ postAuthnWorkflowError, setPostAuthnWorkflowError ] = React.useState();

  React.useEffect(() => {

    async function setup() {
      try {
        const throttlePayload = shouldThrottle(clientId);
        if (!!throttlePayload) {
          logThrottleErrorEvent(
            {
              ...throttlePayload, userId: authState.accessToken.claims.uid,
              email: authState.accessToken.claims.oktaUsername,
            });
          setIsThrottled(true);
        } else {
          //If we're here, the user has already authenticated successfully, so don't prompt again even if degradation happens
          sessionStorageUtil.updateOriginalAuthUrl(
            new URI(sessionStorageUtil.getOriginalAuthUrl())
              .removeQuery('prompt', 'login')
              .toString()
          );
          const { accessToken: { accessToken }, idToken: { idToken, claims: { idp: idpInIDToken } } } = authState;
          const postAuthnRequest = {
            clientId,
            scope: scopeString,
            redirectUri,
            aud,
            emailToken,
            idp,
            fhir,
            authServerId,
            lwaUniqId,
            uiLocales,
            idToken,
            source: 'lwa',
          };

          const apiResponse = await _fetchData(postAuthnRequest, accessToken);
          const configs = enrichApiResponse({
            apiResponse,
            parsedPayload: payload,
          });
          // We never want to prompt again in post authorize as it will ask to perform signin again.
          delete configs.prompt;

          if (configs.idp) {
            // we're going to immediately redirect through to okta's idp authorization flow via the signin-widget
            // without any user interaction so we dont care about being iframe'd for federated idp flows
            allowFraming();
            // If users have previously logged in using the "Login with Athena" (Okta IDP), the subsequent external
            // IDP login may still use the ID token from the original login. This can cause the IDP check to fail.
            // Force user to re-authenticate if the IDP didn't match.
            if ( idpInIDToken && configs.idp !== idpInIDToken) {
              oktaAuth.tokenManager.clear();
              const authorizeToOktaUrl = sessionStorageUtil.getOriginalAuthUrl() + '&prompt=login';
              window.location.assign(authorizeToOktaUrl);
            }
          }
          setPostAuthnConfigs({
            ...configs,
            accessToken,
            idToken,
          });
        }

      } catch (err) {
        setPostAuthnConfigFetchError(err);
      }
    }

    setup();
  }, []);

  React.useEffect(() => {
    if (postAuthnConfigFetchError){
      // eslint-disable-next-line max-len, camelcase
      logPostAuthErrorEvent({ error_message: postAuthnConfigFetchError.message, userId: authState.accessToken.claims.uid });
      throw (postAuthnConfigFetchError);
    }
  }, [ postAuthnConfigFetchError ]);

  React.useEffect(() => {
    const handlePostAuthnDegradeRequest = (event) => {
      setPostAuthnWorkflowError(event.detail);
    };

    const handlePostAuthnFhirRequestError = async (event) => {
      const error = new FhirRequestError(new Error(JSON.stringify(event.detail)));
      logger.error(JSON.stringify(error));
      const errorPayload = {
        error_message: error.message,
        isBackendError: error.isBackendError,
        isOhswError: error.isOhswError,
        source: error.source,
        type: error.type,
      };
      await Promise.all([
        logFhirRequestErrorEvent(errorPayload),
        flushLogs(),
      ]);
    };

    window.addEventListener(POST_AUTHN_DEGRADE_REQUEST, handlePostAuthnDegradeRequest);
    window.addEventListener(POST_AUTHN_FHIR_REQUEST_ERROR, handlePostAuthnFhirRequestError);

    return () => {
      window.removeEventListener(POST_AUTHN_DEGRADE_REQUEST, handlePostAuthnDegradeRequest);
      window.removeEventListener(POST_AUTHN_FHIR_REQUEST_ERROR, handlePostAuthnFhirRequestError);
    };
  }, []);
  if (postAuthnWorkflowError) throw new Error(JSON.stringify(postAuthnWorkflowError));
  if (isThrottled) return <ThrottleError />;
  if (!postAuthnConfigs) return <Loader />;
  return (<>
    <OktaSignInWidget {...postAuthnConfigs} />
    <KeysProber></KeysProber>
  </>);

}
